/**
 * Pure filters gating the sidebar nav and the ⌘K command palette by the
 * current user's effective permissions (Task 5, ERP Users & Access Rights
 * epic) — same "pure builder, tested directly" convention as
 * adminNavItems.ts / configMenuNav.ts, so MainLayout stays untested-mount-free.
 *
 * MainLayout must read entities/reports through these ONCE, before both the
 * sidebar nav items AND the ⌘K command list are built from them — otherwise
 * the two surfaces can disagree about what a restricted member sees.
 *
 * Fail-open here (`undefined` perms -> identity) is deliberate: while
 * useMyPermissions() is still loading, hiding everything is worse than a
 * brief flash of items the server will reject on the actual request — the
 * backend enforces regardless (see permissionHelpers.ts). `showAdministration`
 * is the one exception — it fails CLOSED so the admin-only Users/Roles
 * section never flickers into view for a non-admin before the first
 * response lands.
 */
import { canRead, isUserAdmin, type MyPermissions } from '@/app/permissions/permissionHelpers';
import type { ConfigMenu, ConfigMenuItem } from './configMenuNav';
import type { Subsystem } from './subsystemNav';

function filterByRead<T extends { name: string }>(items: T[], perms: MyPermissions | undefined): T[] {
  if (!perms) return items;
  return items.filter((item) => canRead(perms, item.name));
}

export function filterEntitiesByRead<T extends { name: string }>(
  items: T[],
  perms: MyPermissions | undefined,
): T[] {
  return filterByRead(items, perms);
}

export function filterReportsByRead<T extends { name: string }>(
  items: T[],
  perms: MyPermissions | undefined,
): T[] {
  // Reports are gated on 'read' of the report NAME (core/reports/router.py
  // ::_assert_report_read) — the same rights map/same key as any other
  // object, so this shares filterByRead rather than re-implementing it.
  return filterByRead(items, perms);
}

/**
 * Extract object name from a config menu route for permission checking.
 * Routes like '/app/JournalEntry' -> 'JournalEntry', '/reports/xyz' -> null (reports checked separately).
 * Returns null for non-object routes (like '/dashboard', '/reports'), which are always allowed.
 */
function extractObjectNameFromRoute(route: string | undefined): string | null {
  if (!route) return null;
  const appMatch = route.match(/^\/app\/([^/?#]+)/);
  if (appMatch) return appMatch[1];
  return null;
}

/**
 * Filter a config menu item and its children by readable object permissions.
 * An item is kept if its route doesn't reference an object, or if the user can read that object.
 * Children are filtered recursively.
 */
function filterConfigMenuItem(item: ConfigMenuItem, perms: MyPermissions | undefined): ConfigMenuItem | null {
  // Check if this item's route is readable
  const objectName = extractObjectNameFromRoute(item.route);
  if (objectName && perms && !canRead(perms, objectName)) {
    return null;
  }

  // Filter children recursively
  const filteredChildren = item.children
    ?.map(child => filterConfigMenuItem(child, perms))
    .filter((child): child is ConfigMenuItem => child !== null);

  // If the item itself is readable but has no accessible children, keep it as-is
  return {
    ...item,
    ...(filteredChildren && filteredChildren.length > 0 ? { children: filteredChildren } : {}),
  };
}

/**
 * Filter config menus by removing items that reference objects the user cannot read.
 * Fail-open when perms is undefined (still loading).
 */
export function filterConfigMenusByRead(
  menus: ConfigMenu[],
  perms: MyPermissions | undefined,
): ConfigMenu[] {
  if (!perms) return menus;

  return menus
    .map(menu => {
      const filteredItems = menu.items
        .map(item => filterConfigMenuItem(item, perms))
        .filter((item): item is ConfigMenuItem => item !== null);

      return filteredItems.length > 0 ? { ...menu, items: filteredItems } : null;
    })
    .filter((menu): menu is ConfigMenu => menu !== null);
}

/**
 * Users/Roles administration is gated on user-admin capability, NOT
 * canUpdate('Users') — Task 5's describe_permissions pins
 * objects['Users'] = ['read'] even for a user admin (every active member
 * has implicit read there), so read/update/delete on the Users object is
 * never the right signal. Superusers and full_access members always see it.
 */
export function showAdministration(perms: MyPermissions | undefined, isSuperuser: boolean): boolean {
  if (isSuperuser) return true;
  if (!perms) return false;
  return perms.full_access || isUserAdmin(perms);
}

/**
 * Filter subsystem sections by readable objects. Exact name check — items
 * carry `object_name`, so unlike filterConfigMenusByRead this never parses a
 * route. A section with nothing visible left (own groups AND children) is
 * dropped. Fail-open while perms load, same as every other filter here.
 */
export function filterSubsystemsByRead(
  subsystems: Subsystem[],
  perms: MyPermissions | undefined,
): Subsystem[] {
  if (!perms) return subsystems;
  return subsystems
    .map((subsystem) => {
      const groups = subsystem.groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => !item.object_name || canRead(perms, item.object_name)),
        }))
        .filter((group) => group.items.length > 0);
      const children = filterSubsystemsByRead(subsystem.children, perms);
      if (groups.length === 0 && children.length === 0) return null;
      return { ...subsystem, groups, children };
    })
    .filter((s): s is Subsystem => s !== null);
}
